/// <reference path="Constants.js" />
/**
 * SpinLoader params
 * @typedef {Object} AJAX_OPTIONS
 * @property {string} method Http Request Method.
 * @property {string} url Target url.
 * @property {object} data Data to send.
 * @property {string} mode Fetch API mode {@link JFM_CONST.FETCHMODES}
 * @property {string} contentType Content Type {@link JFM_CONST.CONTENTTYPE}
 * @property {Function} FnSuccess Callback on successful communication.
 * @property {Function} FnError Callback on communication error.
 * @property {Function} FnBeforeSend Fn before establish communication.
 * @property {Function} FnComplete Callback on complete communication.
 */

/**
 * Error response.
 * @typedef {Object} AJAX_ERROR
 * @property {String} message error message.
 * @property {String} stack stack trace.
 * @property {String} status status code.
 * @property {String} statusText statux text.
 */


/**
 * Ajax response.
 * @typedef {Object} AJAX_RESPONSE
 * @property {String} data data response.
 * @property {String} isRedirected is redirected.
 * @property {String} contentType content type header.
 */

/**
 * @class
 * AJAX communications.
 */
class AJAXCommunications_JS {
    /**
     * @param {AJAX_OPTIONS}
     */
    constructor(pOptions) {
        //default values
        this.Options = {
            method: "get",
            url: null,
            data: null,
            mode: JFM_CONST.FETCHMODES.SAMEORIGIN,
            contentType: null,
            FnSuccess: null,
            FnError: null,
            FnBeforeSend: null,
            FnComplete: null,
        };

        Object.assign(this.Options, pOptions);
    }
    /**
     * Send Data to a target url. The response is treated with Callbacks.
     */
    async AsyncFetch() {
        let objThis = this,
            isFinished = false,
            isRedirected = false,
            attempt = 0,
            contentType = '',
            fetchOptions = {
                method: this.Options.method,
                body: this.Options.data,
                redirect: "follow"
            };

        // Add the headers
        if (this.Options.contentType && this.Options.contentType.toLowerCase().indexOf(JFMUI.CONST.CONTENTTYPE.FILEFORM) == -1) {
            fetchOptions.headers = {
                "Content-type": this.Options.contentType,
            };
        }

        fetchOptions.headers = { ...fetchOptions.headers, "X-Requested-With": "XMLHttpRequest" };

        if (objThis.Options.mode == JFM_CONST.FETCHMODES.CORS) {
            fetchOptions.credentials = "include";
        }

        if (this.Options.FnBeforeSend) {
            this.Options.FnBeforeSend();
        }

        do {
            await fetch(this.Options.url, fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            isRedirected = true;
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.JSON) !== -1) {
                            return res.json();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.TEXT) !== -1) {
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.ZIP) !== -1) {
                            return res.blob();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.XEXCEL) !== -1) {
                            return res.blob();
                        } else {
                            return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((dataR) => {
                    isFinished = true;
                    if (this.Options.FnSuccess) {
                        this.Options.FnSuccess(dataR, isRedirected,contentType);
                    }
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        res = !res ? {} : res;
                        if (this.Options.FnError) {
                            this.Options.FnError(res.statusText, res.status, res.message, res.stack);
                        }
                    }
                })
                .finally(() => {
                    if (isFinished) {
                        if (this.Options.FnComplete) {
                            this.Options.FnComplete();
                        }
                    }
                });
        } while (!isFinished);
    }
    /**
     * Send Data to a target url in JSON format.
     * @param {String} pUrl Target url.
     * @param {String} pData Data to Send.
     * @param {String} pMethod Request method.
     * @param {String} pFetchMode Fetch mode.
     * @returns {Promise} Response from fetch API.
     */
     static async AsyncFetchJSON(pUrl ,pData, pMethod, pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN) {
        let isFinished = false,
            isRedirected = false,
            isSuccessful = false,
            attempt = 0,
            contentType = '',
            /**
            * @type {AJAX_RESPONSE}
            */
            objAjaxResponse = null,
            /**
            * @type {AJAX_ERROR}
            */
            objAjaxError = null, 
            fetchOptions = {
                method: pMethod,
                body: !!pData ? JSON.stringify(pData) : null,
                headers: {
                    "Content-type": JFM_CONST.CONTENTTYPE.JSON,
                    "X-Requested-With": "XMLHttpRequest"
                },
                redirect: "follow",
            };

        if (pFetchMode == JFM_CONST.FETCHMODES.CORS) {
            fetchOptions.credentials = "include";
        }


        // Request to the server
        do {
            await fetch(pUrl, fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            isRedirected = true;
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.JSON) !== -1) {
                            return res.json();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.TEXT) !== -1) {
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.ZIP) !== -1) {
                            return res.blob();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.XEXCEL) !== -1) {
                            return res.blob();
                        } else {
                            return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((res) => {
                    isFinished = true;
                    isSuccessful = true;
                    objAjaxResponse =
                    {
                        data: res,
                        isRedirected: isRedirected,
                        contentType: contentType
                    };
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        isSuccessful = false;
                        res = !res ? {} : res;
                        objAjaxError =
                        {
                            message: res.message,
                            stack: res.stack,
                            status: res.status,
                            statusText: res.statusText
                        };
                    }
                });
        } while (!isFinished);

        return isSuccessful ? Promise.resolve(objAjaxResponse) : Promise.reject(objAjaxError);
    }
    /**
     * Fetch Json with Post Request method.
     * @param {String} pUrl Target url.
     * @param {String} pData Data to Send.
     * @param {String} pFetchMode pFetchMode Fetch mode.
     * @returns {Promise} Response from fetch API.
     */
    static async AsyncPostJSON(pUrl, pData, pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN) {
        return AJAXCommunications_JS.AsyncFetchJSON(pUrl, pData, 'Post', pFetchMode);
    }
    /**
     * Fetch Json with Post Request method.
     * @param {String} pUrl Target url.
     * @param {String} pData Data to Send.
     * @param {String} pFetchMode pFetchMode Fetch mode.
     * @returns {Promise} Response from fetch API.
     */
    static async AsyncGetJSON(pUrl, pData, pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN) {
        return AJAXCommunications_JS.AsyncFetchJSON(pUrl, pData, 'Get', pFetchMode);
    }
}